import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AURA_OF_PROTECTION } from '../../../shared/rules/effects/auras.mjs';
import { getFootprintSquares, getMapGridDimensions, tokenAnchorCell } from '../utils/pushGeometryCore.mjs';

const HOLY_NIMBUS = 'paladin:devotion:holy-nimbus';

/**
 * Each active Aura of Protection drawn on the map: a faint gold square around the Paladin's footprint, out to the
 * aura's radius (distances are counted in squares, so the area is square). Holy Nimbus makes it brighter and slowly
 * pulsing; Smite of Protection's Half Cover gives a stronger edge and a shield mark. Hovering shows a note only when
 * the aura changes whether attacks hit (Half Cover's +2 AC). The aura comes from the effect
 * the server projects onto the Paladin itself, so an inactive aura (Incapacitated, 0 HP) draws nothing. It follows the
 * token as it is dragged; who benefits is still decided by the server after the move.
 */
export default function PaladinAuraAreas({ combatState, tokens = [], map }) {
  const { columns, rows } = getMapGridDimensions(map);
  const svg = useRef(null);
  const [hover, setHover] = useState(null);
  const auras = useMemo(() => {
    const effects = combatState?.activeEffects || [];
    return effects.filter(effect => effect?.definitionId === AURA_OF_PROTECTION && String(effect.targetCombatantId) === String(effect.sourceCombatantId))
      .flatMap(effect => {
        const id = String(effect.sourceCombatantId);
        const token = tokens.find(entry => String(entry.characterId) === id);
        const anchor = token && tokenAnchorCell(token.position || token, map);
        if (!anchor) return [];
        const radius = (Number(effect.radiusFeet) || 10) / 5, size = getFootprintSquares(token.size);
        const nimbus = effects.some(other => other?.definitionId === HOLY_NIMBUS && String(other.targetCombatantId) === id);
        const name = token.name || token.label || token.characterName || 'the Paladin';
        const label = effect.halfCover ? `Half Cover (+2 AC) · ${name}'s Smite of Protection` : null;
        return [{ id, x: anchor.x - radius, y: anchor.y - radius, size: size + radius * 2, nimbus, halfCover: effect.halfCover === true, label }];
      });
  }, [combatState?.activeEffects, tokens, map]);
  useEffect(() => {
    const surface = svg.current?.parentElement;
    if (!surface || !auras.length) return undefined;
    const move = event => {
      const rect = svg.current.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const point = { x: (event.clientX - rect.left) / rect.width * columns, y: (event.clientY - rect.top) / rect.height * rows };
      const hits = auras.filter(aura => aura.label && point.x >= aura.x && point.x <= aura.x + aura.size && point.y >= aura.y && point.y <= aura.y + aura.size);
      setHover(hits.length ? { ...point, lines: hits.map(aura => aura.label) } : null);
    };
    const leave = () => setHover(null);
    surface.addEventListener('mousemove', move);
    surface.addEventListener('mouseleave', leave);
    return () => { surface.removeEventListener('mousemove', move); surface.removeEventListener('mouseleave', leave); };
  }, [auras, columns, rows]);
  if (!auras.length) return null;
  const labelWidth = hover ? Math.max(...hover.lines.map(line => line.length)) * .15 + .3 : 0;
  return <svg ref={svg} className="paladin-aura-areas" viewBox={`0 0 ${columns} ${rows}`} preserveAspectRatio="none" aria-label="Paladin auras"
    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: -1 }}>
    {auras.map(aura => <g key={aura.id} data-paladin-aura={aura.id} data-holy-nimbus={aura.nimbus ? 'true' : undefined} data-half-cover={aura.halfCover ? 'true' : undefined}>
      <rect className={aura.nimbus ? 'paladin-aura__fill paladin-aura__fill--nimbus' : 'paladin-aura__fill'} x={aura.x} y={aura.y} width={aura.size} height={aura.size} rx=".35"
        fill={aura.nimbus ? '#ffc94d' : '#f6d365'} fillOpacity={aura.nimbus ? '.112' : '.056'}
        stroke={aura.nimbus ? '#ffcf5a' : '#f6d365'} strokeOpacity={aura.halfCover ? '.665' : aura.nimbus ? '.49' : '.315'} strokeWidth={aura.halfCover ? '.1' : '.05'} />
      {aura.halfCover && <path aria-label="Smite of Protection: Half Cover" transform={`translate(${aura.x + aura.size - .55},${aura.y + .25})`}
        d="M.15 0 L.3 .06 Q.3 .26 .15 .34 Q0 .26 0 .06 Z" fill="#fff3c4" fillOpacity=".63" stroke="#c9972b" strokeWidth=".025" />}
    </g>)}
    {hover && <g transform={`translate(${Math.max(.1, Math.min(hover.x + .2, columns - labelWidth - .1))},${Math.max(.1, hover.y - .4 - hover.lines.length * .42)})`}>
      <rect width={labelWidth} height={hover.lines.length * .42 + .14} rx=".12" fill="#151d30" stroke="#d8b65a" strokeWidth=".025" />
      {hover.lines.map((line, index) => <text key={line} x={labelWidth / 2} y={.38 + index * .42} textAnchor="middle" fill="#fff3c4" fontSize=".26">{line}</text>)}
    </g>}
  </svg>;
}
